import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { aiFeedbackReasonSchema } from "@io-spoke/contracts/aiFeedback";
import { Button } from "@components/ui/button";
import { Label } from "@components/ui/label";
import { FormChoiceSelect } from "@components/ui/choice-select";
import { Textarea } from "@components/ui/textarea";
import { getAiFeedback, submitAiFeedback } from "../../lib/aiFeedback";

const negativeFeedbackSchema = z.object({
  reason: aiFeedbackReasonSchema,
  correction: z.string().trim().max(2_000).optional(),
});
type NegativeFeedbackValues = z.infer<typeof negativeFeedbackSchema>;

export function AiFeedbackControl({ responseId }: { responseId: string }) {
  const [choice, setChoice] = useState<"up" | "down" | null>(null);
  const feedbackAction = useAsyncAction();
  const [votingUp, setVotingUp] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const {
    register, control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NegativeFeedbackValues>({
    resolver: zodResolver(negativeFeedbackSchema),
    defaultValues: { reason: "incorrect_conclusion", correction: "" },
  });

  useEffect(() => {
    let active = true;
    void getAiFeedback(responseId).then((feedback) => {
      if (!active || !feedback) return;
      setChoice(feedback.rating);
      reset({ reason: feedback.reason ?? "incorrect_conclusion", correction: feedback.correction ?? feedback.comment ?? "" });
    }).catch(() => undefined);
    return () => { active = false; };
  }, [responseId, reset]);

  const voteUp = async () => feedbackAction.run(async () => {
    setVotingUp(true);
    setMessage(null);
    try {
      await submitAiFeedback(responseId, { rating: "up" });
      setChoice("up");
      setMessage("Thanks—feedback recorded for evaluation.");
    } catch {
      setMessage("Feedback could not be saved. Try again.");
    } finally {
      setVotingUp(false);
    }
  });

  const submitDown = async (values: NegativeFeedbackValues) => feedbackAction.run(async () => {
    setMessage(null);
    try {
      await submitAiFeedback(responseId, { rating: "down", reason: values.reason, correction: values.correction?.trim() || null });
      setMessage("Thanks—your correction will be included in evaluation.");
    } catch {
      setMessage("Feedback could not be saved. Try again.");
    }
  });

  return (
    <div className="rounded-lg border bg-muted/20 p-4" aria-label="Rate this AI proposal">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Was this proposal useful?</p>
          <p className="text-xs text-muted-foreground">Feedback improves evaluations; it never changes the model automatically.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={choice === "up" ? "default" : "outline"} loading={votingUp} loadingLabel="Saving feedback…" aria-label="Helpful proposal" aria-pressed={choice === "up"} disabled={feedbackAction.pending || isSubmitting} onClick={() => void voteUp()}><ThumbsUp className="h-4 w-4" />Helpful</Button>
          <Button type="button" size="sm" variant={choice === "down" ? "default" : "outline"} aria-label="Not helpful proposal" aria-pressed={choice === "down"} disabled={feedbackAction.pending || isSubmitting} onClick={() => setChoice("down")}><ThumbsDown className="h-4 w-4" />Not helpful</Button>
        </div>
      </div>
      {choice === "down" ? (
        <form className="mt-4 space-y-3 border-t pt-4" onSubmit={handleSubmit(submitDown)} noValidate>
          <div className="space-y-2">
            <Label htmlFor={`feedback-reason-${responseId}`}>What needs improvement?</Label>
            <FormChoiceSelect id={`feedback-reason-${responseId}`} control={control} name="reason" disabled={isSubmitting} options={[{ value: "incorrect_conclusion", label: "Incorrect conclusion" }, { value: "missing_context", label: "Missing context" }, { value: "wrong_owner", label: "Wrong owner" }, { value: "wrong_priority", label: "Wrong priority" }, { value: "not_actionable", label: "Not actionable" }, { value: "other", label: "Other" }]} />
            {errors.reason ? <p role="alert" className="text-xs text-destructive">{errors.reason.message}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor={`feedback-correction-${responseId}`}>Correction or comment <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea id={`feedback-correction-${responseId}`} maxLength={2000} {...register("correction")} disabled={isSubmitting} placeholder="What should IO Spoke have concluded?" aria-invalid={Boolean(errors.correction)} aria-describedby={errors.correction ? `feedback-correction-${responseId}-error` : undefined} />
            {errors.correction ? <p id={`feedback-correction-${responseId}-error`} role="alert" className="text-xs text-destructive">{errors.correction.message}</p> : null}
          </div>
          <Button type="submit" size="sm" loading={isSubmitting} loadingLabel="Saving feedback…">Submit feedback</Button>
        </form>
      ) : null}
      {message ? <p className="mt-3 text-xs text-muted-foreground" role="status" aria-live="polite">{message}</p> : null}
    </div>
  );
}
